import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Typography,
  message,
  Empty,
  Form,
  Input,
  Select,
  Col,
  DatePicker,
  Upload,
  Modal,
} from "antd";
import {
  ExportOutlined,
  ReloadOutlined,
  UploadOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import FilterPanel from "../../components/common/FilterPanel";
import ItemMasterDataTable from "./components/ItemMasterDataTable";
import BatchActionToolbar from "../../components/common/BatchActionToolbar";
import {
  ItemMasterData,
  ItemMasterDataFilters,
} from "../../types/item-master-data";
import { itemMasterDataService } from "../../services/itemMasterDataService";
import categoryService from "../../services/categoryService";
import ItemDetailModal from "./components/ItemDetailModal";
import DisplayConfigModal from "./components/DisplayConfigModal";
import dayjs from "dayjs";
import {
  ITEM_TYPE_OPTIONS,
  STATUS_OPTIONS,
  DISPLAY_TYPE_OPTIONS,
  STOCK_UNIT_OPTIONS,
  BOOLEAN_OPTIONS,
} from "../../utils/itemMasterDataConstants";

// 扩展分页参数接口，添加total属性用于UI分页
interface ExtendedPaginationParams {
  current: number;
  pageSize: number;
  total: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ItemMasterDataList: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // 为DISPLAY_TYPE_OPTIONS添加翻译
  const translatedDisplayTypeOptions = DISPLAY_TYPE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  // 为STATUS_OPTIONS添加翻译
  const translatedStatusOptions = STATUS_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  // 为ITEM_TYPE_OPTIONS添加翻译
  const translatedItemTypeOptions = ITEM_TYPE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  // 为BOOLEAN_OPTIONS添加翻译
  const translatedBooleanOptions = BOOLEAN_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
  }));

  // State for items data
  const [items, setItems] = useState<ItemMasterData[]>([]);

  // State for loading indicators
  const [loading, setLoading] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  // State for filters and pagination
  const [filters, setFilters] = useState<ItemMasterDataFilters>({});
  const [pagination, setPagination] = useState<ExtendedPaginationParams>({
    current: 1,
    pageSize: 20,
    total: 0,
    sortBy: "gmtCreate",
    sortOrder: "desc",
  });

  // State for selected rows
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // 详情弹窗状态
  const [selectedItem, setSelectedItem] = useState<ItemMasterData | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);

  // 陈列参数配置弹窗状态
  const [displayConfigModalVisible, setDisplayConfigModalVisible] =
    useState<boolean>(false);
  const [configItem, setConfigItem] = useState<ItemMasterData | null>(null);
  const [displayConfigLoading, setDisplayConfigLoading] =
    useState<boolean>(false);

  // 品牌选项状态
  const [brandOptions, setBrandOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);

  // 类目选项状态
  const [divisionOptions, setDivisionOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [classOptions, setClassOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [subClassOptions, setSubClassOptions] = useState<
    Array<{ value: any; label: string }>
  >([]);

  // 导入功能状态
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<any[]>([]);

  // 获取品牌选项
  useEffect(() => {
    const fetchBrandOptions = async () => {
      try {
        const response = await itemMasterDataService.getBrandOptions();
        if (response.success && response.data) {
          setBrandOptions(response.data);
        }
      } catch (error) {
        console.error("获取品牌选项失败:", error);
      }
    };

    fetchBrandOptions();
  }, []);

  // 获取事业部选项
  useEffect(() => {
    const fetchDivisionOptions = async () => {
      try {
        const response = await categoryService.listDivision();
        if (response.success && response.data) {
          setDivisionOptions(response.data);
        }
      } catch (error) {
        console.error("获取事业部选项失败:", error);
      }
    };

    fetchDivisionOptions();
    fetchClassOptions();
    fetchSubClassOptions();
  }, []);

  // 获取类别选项
  const fetchClassOptions = async (
    divisionCode?: string,
    classCode?: string
  ) => {
    try {
      const response = await categoryService.listClass({
        divisionCode,
        searchStr: classCode,
      });
      if (response.success && response.data) {
        setClassOptions(response.data);
      } else {
        setClassOptions([]);
      }
    } catch (error) {
      console.error("获取类别选项失败:", error);
      setClassOptions([]);
    }
  };

  // 获取子类别选项
  const fetchSubClassOptions = async (
    divisionCode?: string,
    classCode?: string,
    subClassCode?: string
  ) => {
    try {
      const response = await categoryService.listSubClass({
        divisionCode,
        classCode,
        searchStr: subClassCode,
      });
      if (response.success && response.data) {
        const subClass =
          response.data?.map((item: any, index) => ({
            value: index,
            label: item.label,
            item,
          })) || [];
        setSubClassOptions(subClass);
      } else {
        setSubClassOptions([]);
      }
    } catch (error) {
      console.error("获取子类别选项失败:", error);
      setSubClassOptions([]);
    }
  };

  // Fetch items data
  const fetchItems = async (refresh = false) => {
    setLoading(true);
    try {
      // If refresh is true, reset data first
      if (refresh) {
        await itemMasterDataService.getItemMasterDataPage(
          {},
          {
            currentPage: 1,
            pageSize: pagination.pageSize,
          }
        );
      }
      let req = {
        ...filters,
        category5Id: subClassOptions?.[filters.category5Id]?.item?.value,
      };

      const response = await itemMasterDataService.getItemMasterDataPage(req, {
        currentPage: pagination.current,
        pageSize: pagination.pageSize,
        sortBy: pagination.sortBy,
        sortOrder: pagination.sortOrder === "asc" ? "ascend" : "descend",
      });

      if (response.success && response.data) {
        setItems(response.data.tableData || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data?.paging?.totalCount || 0,
        }));
      } else {
        message.error(response.errorMessage || t("itemMasterData.loadFailed"));
        setItems([]);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
      message.error(t("itemMasterData.loadFailed"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch
  // useEffect(() => {
  //   fetchItems();
  // }, []);

  // Fetch data when filters or pagination change
  useEffect(() => {
    fetchItems();
  }, [
    filters,
    pagination.current,
    pagination.pageSize,
    pagination.sortBy,
    pagination.sortOrder,
  ]);

  // Handle filter change
  const handleFilterChange = (newFilters: ItemMasterDataFilters) => {
    // 处理时间字段，将其转换为时间戳格式
    const processedFilters = {
      ...newFilters,
      firstReceivedTimeStart: newFilters.firstReceivedTime?.[0]
        ? dayjs(newFilters.firstReceivedTime[0]).startOf('day').valueOf()
        : undefined,
      firstReceivedTimeEnd: newFilters.firstReceivedTime?.[1]
        ? dayjs(newFilters.firstReceivedTime[1]).endOf('day').valueOf()
        : undefined,
      createTimeStart: newFilters.createTimeRange?.[0]
        ? dayjs(newFilters.createTimeRange[0]).startOf('day').valueOf()
        : undefined,
      createTimeEnd: newFilters.createTimeRange?.[1]
        ? dayjs(newFilters.createTimeRange[1]).endOf('day').valueOf()
        : undefined,
    };

    setFilters(processedFilters);
    // Reset to first page when filters change
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  };

  // Handle filter reset
  const handleFilterReset = () => {
    // 重置时需要特别处理时间字段，确保它们被正确重置为undefined而不是时间戳
    setFilters({});
    setPagination((prev) => ({
      ...prev,
      current: 1,
      sortBy: "gmtCreate",
      sortOrder: "desc",
    }));
    // 重置表单字段
    form.resetFields();
    // 重置类目选项
    // setClassOptions([]);
    // setSubClassOptions([]);
    // 重新获取事业部选项
    categoryService.listDivision().then((response) => {
      if (response.success && response.data) {
        setDivisionOptions(response.data);
      }
    });
    categoryService.listClass().then((response) => {
      if (response.success && response.data) {
        setClassOptions(response.data);
      }
    });
    categoryService.listSubClass().then((response) => {
      if (response.success && response.data) {
        const subClass =
          response.data?.map((item: any, index) => ({
            value: index,
            label: item.label,
            item,
          })) || [];
        setSubClassOptions(subClass);
      }
    });
    // fetchItems(false);
  };

  // Handle page change
  const handlePageChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  // Handle sort change
  const handleSortChange = (sortBy: string, sortOrder: "asc" | "desc") => {
    setPagination((prev) => ({
      ...prev,
      sortBy,
      sortOrder,
    }));
  };

  // Handle export
  const handleExport = async () => {
    setExportLoading(true);
    try {
      message.loading(t("itemMasterData.messages.exportingData"), 0);
      const response = await itemMasterDataService.exportItemData(filters);

      // 模拟下载文件
      const link = document.createElement("a");
      if (response.data) {
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
      } else {
        message.error(t("storeGroup.exportDataFormatError"));
        return;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.destroy();
      message.success(t("common.exportSuccess"));
    } catch (error) {
      message.destroy();
      console.error("Export error:", error);
      message.error(t("itemMasterData.exportFailed"));
    } finally {
      setExportLoading(false);
    }
  };

  // Handle view item detail
  const handleViewItem = async (itemCode: string) => {
    try {
      const response = await itemMasterDataService.getItemMasterDataDetail(
        itemCode
      );
      if (response.success && response.data) {
        setSelectedItem(response.data);
        setDetailModalVisible(true);
      } else {
        message.error(
          response.errorMessage || t("itemMasterData.messages.getDetailFailed")
        );
      }
    } catch (error) {
      console.error("获取商品详情失败:", error);
      message.error(t("itemMasterData.messages.getDetailFailed"));
    }
  };

  // Handle configure display parameters
  const handleConfigDisplay = async (itemCode: string) => {
    try {
      const response = await itemMasterDataService.getItemMasterDataDetail(
        itemCode
      );
      if (response.success && response.data) {
        setConfigItem(response.data);
        setDisplayConfigModalVisible(true);
      } else {
        message.error(
          response.errorMessage || t("itemMasterData.messages.getInfoFailed")
        );
      }
    } catch (error) {
      console.error("获取商品信息失败:", error);
      message.error(t("itemMasterData.messages.getInfoFailed"));
    }
  };

  // Handle save display config
  const handleSaveDisplayConfig = async (
    itemCode: string,
    updatedItem: ItemMasterData
  ) => {
    setDisplayConfigLoading(true);
    try {
      // 这里需要调用服务来保存更新后的商品数据
      // 由于我们已经修改了数据结构，现在直接保存整个ItemMasterData对象
      const response = await itemMasterDataService.saveDisplayConfig(
        itemCode,
        updatedItem
      );
      if (response.success) {
        // 刷新列表数据
        fetchItems();
        setDisplayConfigModalVisible(false);
        setConfigItem(null);
      } else {
        throw new Error(
          response.errorMessage || t("itemMasterData.messages.saveFailed")
        );
      }
    } catch (error: any) {
      console.error("保存陈列参数配置失败:", error);
      throw error; // 重新抛出错误，由组件处理
    } finally {
      setDisplayConfigLoading(false);
    }
  };

  // Clear selected rows
  const handleClearSelection = () => {
    setSelectedRowKeys([]);
  };

  // Handle batch export
  const handleBatchExport = async () => {
    if (selectedRowKeys.length === 0) return;

    try {
      setExportLoading(true);
      message.loading(
        t("itemMasterData.messages.exportingSelectedItems", {
          count: selectedRowKeys.length,
        }),
        0
      );

      const selectedFilters = {
        ...filters,
        itemCodes: selectedRowKeys,
      };

      const response = await itemMasterDataService.exportItemData(
        selectedFilters
      );

      // 模拟下载文件
      const link = document.createElement("a");
      if (response.data) {
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
      } else {
        message.error(t("storeGroup.exportDataFormatError"));
        return;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.destroy();
      message.success(
        t("itemMasterData.messages.exportSelectedSuccess", {
          count: selectedRowKeys.length,
        })
      );
      handleClearSelection();
    } catch (error) {
      message.destroy();
      console.error("Batch export error:", error);
      message.error(t("itemMasterData.exportFailed"));
    } finally {
      setExportLoading(false);
    }
  };

  // Handle import display config
  const handleImportDisplayConfig = () => {
    setImportModalVisible(true);
  };

  // Handle import file upload
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(t("itemMasterData.import.selectFileFirst"));
      return;
    }

    const file = fileList[0];
    setImportLoading(true);

    try {
      message.loading(t("itemMasterData.import.uploading"), 0);
      const response = await itemMasterDataService.importDisplayConfig(file);

      if (response.success) {
        message.destroy();
        message.success(t("itemMasterData.import.success"));
        setImportModalVisible(false);
        setFileList([]);
        fetchItems(); // 刷新列表

        if (response.data) {
          const link = document.createElement("a");
          link.href = response.data.downloadUrl;
          link.download = response.data.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        message.destroy();
        message.error(
          response.errorMessage || t("itemMasterData.import.failed")
        );
      }
    } catch (error) {
      message.destroy();
      console.error("Import error:", error);
      message.error(t("itemMasterData.import.failed"));
    } finally {
      setImportLoading(false);
    }
  };

  // Handle import modal cancel
  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
  };

  // Upload props
  const uploadProps = {
    accept: ".xlsx,.xls,.csv",
    fileList,
    beforeUpload: (file: any) => {
      // 验证文件类型
      const isValidType =
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.name.endsWith(".csv");
      if (!isValidType) {
        message.error(t("itemMasterData.import.invalidFileType"));
        return false;
      }

      // 验证文件大小 (10MB)
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t("itemMasterData.import.fileTooLarge"));
        return false;
      }

      setFileList([file]);
      return false; // 阻止自动上传
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  return (
    <div>
      {/* 替换ItemFilterPanel为通用FilterPanel */}
      <FilterPanel
        form={form}
        // initialValues={filterPanelInitialValues}
        onFilter={(values) => {
          // 处理时间字段，将其转换为时间戳格式
          const processedValues = {
            ...values,
            firstReceivedTime: values.firstReceivedTime,
            createTimeRange: values.createTimeRange,
          };
          handleFilterChange(processedValues);
        }}
        onReset={handleFilterReset}
        className="item-filter-panel"
        title={t("menu.item")}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item name="search" label={t("common.search")}>
            <Input
              placeholder={t("itemMasterData.filter.searchPlaceholder")}
              allowClear
              disabled={loading}
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="itemCode"
            label={t("itemMasterData.filter.itemCode")}
          >
            <Input
              placeholder={t("itemMasterData.filter.itemCode")}
              allowClear
              disabled={loading}
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="itemName"
            label={t("itemMasterData.filter.itemName")}
          >
            <Input
              placeholder={t("itemMasterData.filter.itemName")}
              allowClear
              disabled={loading}
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name="status" label={t("itemMasterData.filter.status")}>
            <Select
              placeholder={t("itemMasterData.filter.status")}
              allowClear
              disabled={loading}
            >
              {translatedStatusOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* <Col span={6}>
          <Form.Item
            name="itemType"
            label={t("itemMasterData.filter.itemType")}
          >
            <Select
              placeholder={t("itemMasterData.filter.itemType")}
              allowClear
              disabled={loading}
            >
              {translatedItemTypeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col> */}

        <Col span={6}>
          <Form.Item
            name="brandName"
            label={t("itemMasterData.filter.brandName")}
          >
            <Select
              placeholder={t("itemMasterData.filter.brandName")}
              allowClear
              showSearch
              optionFilterProp="children"
              disabled={loading}
            >
              {brandOptions.map((brand) => (
                <Select.Option key={brand.value} value={brand.label}>
                  {brand.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="displayType"
            label={t("itemMasterData.filter.displayType")}
          >
            <Select
              placeholder={t("itemMasterData.filter.displayType")}
              allowClear
              disabled={loading}
            >
              {translatedDisplayTypeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="strategicFlag"
            label={t("itemMasterData.filter.strategicFlag")}
          >
            <Select
              placeholder={t("itemMasterData.filter.strategicFlag")}
              allowClear
              disabled={loading}
            >
              {translatedBooleanOptions.map((option) => (
                <Select.Option key={String(option.value)} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="firstReceivedTime"
            label={t("itemMasterData.filter.firstReceivedTime")}
          >
            <RangePicker
              style={{ width: '100%' }}
              placeholder={[
                t("itemMasterData.filter.firstReceivedTimeStart"),
                t("itemMasterData.filter.firstReceivedTimeEnd")
              ]}
              disabled={loading}
            />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item name="hasEdit" label={t("itemMasterData.filter.hasEdit")}>
            <Select
              placeholder={t("itemMasterData.filter.hasEdit")}
              allowClear
              disabled={loading}
            >
              {translatedBooleanOptions.map((option) => (
                <Select.Option key={String(option.value)} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* 新增的查询条件 */}
        <Col span={6}>
          <Form.Item
            name="category1Id"
            label={t("itemMasterData.filter.division")}
          >
            <Select
              placeholder={t("itemMasterData.filter.division")}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                // 支持对value和label的过滤
                const value = option?.value?.toString() || "";
                const label = option?.children?.toString() || "";
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              disabled={loading}
              onChange={(value) => {
                // 清空class和subclass选项
                setClassOptions([]);
                setSubClassOptions([]);
                form.setFieldsValue({
                  classCode: undefined,
                  subClassCode: undefined,
                });

                // 获取class选项
                if (value) {
                  fetchClassOptions(value);
                }
              }}
            >
              {divisionOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="category4Id"
            label={t("itemMasterData.filter.class")}
          >
            <Select
              placeholder={t("itemMasterData.filter.class")}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                // 支持对value和label的过滤
                const value = option?.value?.toString() || "";
                const label = option?.children?.toString() || "";
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              disabled={loading}
              onChange={(value) => {
                // 清空subclass选项
                setSubClassOptions([]);
                form.setFieldsValue({
                  subClassCode: undefined,
                });

                // 获取subclass选项
                if (value) {
                  const divisionCode = form.getFieldValue("divisionCode");
                  if (divisionCode) {
                    fetchSubClassOptions(divisionCode, value);
                  }
                }
              }}
            >
              {classOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="category5Id"
            label={t("itemMasterData.filter.subclass")}
          >
            <Select
              placeholder={t("itemMasterData.filter.subclass")}
              allowClear
              showSearch
              optionFilterProp="children"
              options={subClassOptions}
              onChange={(index, selectData: any) => {
                // console.log("....", selectData);
                // form.setFieldValue("category5Id", selectData?.item?.value);
              }}
              filterOption={(input, option) => {
                // 支持对value和label的过滤
                // 自定义过滤
                const value = option?.item?.value?.toString() || "";
                const label = option?.label?.toString() || "";
                return (
                  label.toLowerCase().includes(input.toLowerCase()) ||
                  value.toLowerCase().includes(input.toLowerCase())
                );
              }}
              disabled={loading}
            >
              {/* {subClassOptions.map((option, index) => (
                <Select.Option key={index} value={option.value}>
                  {option.label}
                </Select.Option>
              ))} */}
            </Select>
          </Form.Item>
        </Col>

        {/* 新增的创建时间范围查询条件 */}
        <Col span={12}>
          <Form.Item
            name="createTimeRange"
            label={t("itemMasterData.filter.createTimeRange")}
          >
            <RangePicker
              style={{ width: '100%' }}
              placeholder={[
                t("itemMasterData.filter.createTimeStart"),
                t("itemMasterData.filter.createTimeEnd")
              ]}
              disabled={loading}
            />
          </Form.Item>
        </Col>
      </FilterPanel>

      <Card className="table-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <BatchActionToolbar
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: "export",
                label: t("itemMasterData.batchExport"),
                icon: <ExportOutlined />,
                onClick: handleBatchExport,
                disabled: loading || exportLoading,
              },
            ]}
            onClearSelection={handleClearSelection}
          />
          <Space>
            <Button
              icon={<UploadOutlined />}
              onClick={handleImportDisplayConfig}
              disabled={loading}
            >
              {t("itemMasterData.import.title")}
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
              loading={exportLoading}
              disabled={loading}
            >
              {t("itemMasterData.export")}
            </Button>
            {/* <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchItems(true)}
              loading={loading}
            >
              {t('itemMasterData.refresh')}
            </Button> */}
          </Space>
        </div>

        <ItemMasterDataTable
          data={items}
          pagination={pagination}
          loading={loading}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          onRefresh={() => fetchItems()}
          onView={handleViewItem}
          onConfigDisplay={handleConfigDisplay}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        />
      </Card>

      <ItemDetailModal
        visible={detailModalVisible}
        item={selectedItem}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedItem(null);
        }}
      />

      <DisplayConfigModal
        visible={displayConfigModalVisible}
        item={configItem}
        onClose={() => {
          setDisplayConfigModalVisible(false);
          setConfigItem(null);
        }}
        onSave={handleSaveDisplayConfig}
        loading={displayConfigLoading}
      />

      {/* 导入对话框 */}
      <Modal
        title={t("itemMasterData.import.title")}
        open={importModalVisible}
        onOk={handleUpload}
        onCancel={handleImportCancel}
        confirmLoading={importLoading}
        width={600}
      >
        <div style={{ marginBottom: "16px" }}>
          <p>{t("itemMasterData.import.description")}</p>
          <p style={{ color: "#666", fontSize: "12px" }}>
            {t("itemMasterData.import.supportedFormats")}
          </p>
        </div>

        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {t("itemMasterData.import.dragText")}
          </p>
          <p className="ant-upload-hint">{t("itemMasterData.import.hint")}</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default ItemMasterDataList;
